import { SignInPage } from '@backstage/core-components';

import { discoveryApiRef, useApi } from '@backstage/core-plugin-api';
import { AppComponents } from '@backstage/core-app-api';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  DBaaSAppPicker,
  DBaaSAppPickerSchema,
  ExistingEntityPicker,
  FormContextFieldValue,
  GroupEntityPicker,
  OwnedITRCAppPicker,
  OwnedITRCAppPickerSchema,
  OwnedRepoUrlPicker,
} from '../components';
import { scheduleTokenCookieRefresh } from '../auth/cookieAuth';
import {
  ApiDropdownComponent,
  ApiDropdownComponentSchema,
} from '../components/scaffolder/ApiDropdown';
import {
  ApiMultiSelectComponent,
  ApiMultiSelectSchema,
} from '../components/scaffolder/ApiMultiSelect';
import { devhubAuthApiRef } from '../../apis';
import {
  ActiveDirectoryGroupPicker,
  ActiveDirectoryGroupPickerSchema,
} from '../components/scaffolder/ActiveDirectoryGroupPicker';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import {
  AzureActiveDirectoryGroupPicker,
  AzureActiveDirectoryGroupPickerSchema,
} from '../components/scaffolder/AzureActiveDirectoryGroupPicker';
import {
  ActiveDirectoryGroupPickerMulti,
  ActiveDirectoryGroupPickerMultiSchema,
} from '../components/scaffolder/ActiveDirectoryGroupPickerMulti';
import {
  OCPConfigFilePicker,
  OCPConfigFilePickerSchema,
} from '../components/scaffolder/OCPConfigFilePicker';
import {
  IconCardPicker,
  IconCardPickerSchema,
} from '../components/scaffolder/IconCardPicker';
import {
  ApplicationPicker,
  ApplicationPickerSchema,
} from '../components/scaffolder/ApplicationPicker';
import {
  ArtifactoryPermissionPicker,
  ArtifactoryPermissionPickerSchema,
} from '../components/scaffolder/ArtifactoryPermissionPicker';
import {
  ArtifactoryProjectPicker,
  ArtifactoryProjectPickerSchema,
} from '../components/scaffolder/ArtifactoryProjectPicker';

/**
 * A collection of custom components
 */
export const customComponents: Partial<AppComponents> = {
  SignInPage: props => {
    const discoveryApi = useApi(discoveryApiRef);
    const devHubAzureAdAuthApi = useApi(devhubAuthApiRef);
    return (
      <SignInPage
        {...props}
        provider={{
          id: 'microsoft',
          title: 'Login',
          message: 'Please click SIGN IN to continue to DevHub',
          apiRef: devhubAuthApiRef,
        }}
        onSignInSuccess={async identityApi => {
          scheduleTokenCookieRefresh(
            await discoveryApi.getBaseUrl('cookie'),
            devHubAzureAdAuthApi,
          );

          props.onSignInSuccess(identityApi);
        }}
      />
    );
  },
};

/**
 * A scaffolder plugin for {@link FormContextFieldValue}
 */
export const FormContextFieldValueExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'FormContextFieldValue',
    component: FormContextFieldValue,
  }),
);

/**
 * A scaffolder plugin for {@link ExistingEntityPicker}
 */
export const ExistingEntityPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ExistingEntityPicker',
    component: ExistingEntityPicker,
  }),
);

/**
 * A scaffolder plugin for {@link GroupEntityPickerExtension}
 */
export const GroupEntityPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'GroupEntityPicker',
    component: GroupEntityPicker,
  }),
);

/**
 * A scaffolder plugin for {@link DBaaSAppPicker}
 */
export const DBaaSAppPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'DBaaSAppPicker',
    component: DBaaSAppPicker,
    schema: DBaaSAppPickerSchema,
  }),
);

/**
 * A scaffolder plugin for {@link OwnedITRCAppPicker}
 */
export const OwnedITRCAppPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'OwnedITRCAppPicker',
    component: OwnedITRCAppPicker,
    schema: OwnedITRCAppPickerSchema,
  }),
);

/**
 * A scaffolder plugin for {@link OwnedRepoUrlPicker}
 */
export const OwnedRepoUrlPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'OwnedRepoUrlPicker',
    component: OwnedRepoUrlPicker,
  }),
);

/**
 * A scaffolder plugin for {@link OwnedRepoUrlPicker}
 */
export const ApiDropdownComponentExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ApiDropdownComponent',
    component: ApiDropdownComponent,
    schema: ApiDropdownComponentSchema,
  }),
);

/**
 * A scaffolder plugin for {@link ApiMultiSelectComponent}
 */
export const ApiMultiSelectComponentExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ApiMultiSelectComponent',
    component: ApiMultiSelectComponent,
    schema: ApiMultiSelectSchema,
  }),
);

/**
 * A scaffolder plugin for {@link ActiveDirectoryGroupPicker}
 */
export const ActiveDirectoryGroupPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ActiveDirectoryGroupPicker',
    component: ActiveDirectoryGroupPicker,
    schema: ActiveDirectoryGroupPickerSchema,
  }),
);

/**
 * A scaffolder plugin for {@link ActiveDirectoryGroupPicker}
 */
export const AzureActiveDirectoryGroupPickerExtension =
  scaffolderPlugin.provide(
    createScaffolderFieldExtension({
      name: 'AzureActiveDirectoryGroupPicker',
      component: AzureActiveDirectoryGroupPicker,
      schema: AzureActiveDirectoryGroupPickerSchema,
    }),
  );

/**
 * A scaffolder plugin for {@link ActiveDirectoryGroupPickerMulti}
 */
export const ActiveDirectoryGroupPickerMultiExtension =
  scaffolderPlugin.provide(
    createScaffolderFieldExtension({
      name: 'ActiveDirectoryGroupPickerMulti',
      component: ActiveDirectoryGroupPickerMulti,
      schema: ActiveDirectoryGroupPickerMultiSchema,
    }),
  );

/**
 * A scaffolder plugin for {@link IconCardPicker}
 */
export const IconCardPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'IconCardPicker',
    component: IconCardPicker,
    schema: IconCardPickerSchema,
  }),
);

/**
 * A scaffolder plugin for {@link ApplicationPicker}
 */
export const ApplicationPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ApplicationPicker',
    component: ApplicationPicker,
    schema: ApplicationPickerSchema,
  }),
);

/**
 * A scaffolder plugin for {@link ArtifactoryPermissionPicker}
 */
export const ArtifactoryPermissionPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ArtifactoryPermissionPicker',
    component: ArtifactoryPermissionPicker,
    schema: ArtifactoryPermissionPickerSchema,
  }),
);

/**
 * A scaffolder plugin for {@link ArtifactoryProjectPicker}
 */
export const ArtifactoryProjectPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ArtifactoryProjectPicker',
    component: ArtifactoryProjectPicker,
    schema: ArtifactoryProjectPickerSchema,
  }),
);

/**
 * A scaffolder plugin for {@link OCPConfigFilePicker}
 */
export const OCPConfigFilePickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'OCPConfigFilePicker',
    component: OCPConfigFilePicker,
    schema: OCPConfigFilePickerSchema,
    validation: (value, validationContext) => {
      // value = whatever your component passed via onChange
      const errors: string[] = [];

      if (value?.metrics?.enablemetric) {
        // metrics file validation
        if (
          value.metrics &&
          (!value.metrics.metricrawurl || value.metrics.metricrawurl === '')
        ) {
          errors.push("URL must not be empty if 'Enable Metrics' is checked");
        } else if (
          !/^https:\/\/raw\.githubusercontent\.com\/.*\.yaml$/.test(
            value.metrics.metricrawurl,
          )
        ) {
          errors.push(
            "URL must start with 'https://raw.githubusercontent.com/' and end with '.yaml'",
          );
        }

        // ingest URL validation
        if (value.metrics?.ingestenabled) {
          if (!value.metrics?.ingestUrl) {
            errors.push('Ingest URL is required');
          } else if (!value.metrics.ingestUrl.startsWith('https://ingest')) {
            errors.push("Ingest URL must start with 'https://ingest'");
          }
        }
      }

      if (errors.length > 0) {
        // 👇 attaches errors to the field, blocks submission
        validationContext.addError(errors.join('\n'));
      }
    },
  }),
);
